import { useState, useEffect } from 'react';
import { Problem } from '../../types';
import { SEVERITY_COLORS } from '../../constants/problemVisuals';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useReportTheme } from '../../contexts/ReportThemeContext';
import { BulletPointText } from '../common/BulletPointText';
import { GrTest } from 'react-icons/gr';
import { FaFilePrescription } from 'react-icons/fa6';
import { useTranslate } from '../../hooks/useTranslate';

interface ProblemCardProps {
    problem: Problem;
    index: number;
    isExpanded: boolean;
    onToggle: (id: string) => void;
    isAnimated: boolean;
}

export function ProblemCard({ problem, isExpanded, onToggle, isAnimated }: ProblemCardProps) {
    const [mounted, setMounted] = useState(false);
    const { getThemeClasses } = useReportTheme();
    const themeClasses = getThemeClasses();
    const { t } = useTranslate();
    useEffect(() => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => setMounted(true));
        });
    }, []);
    const reduced = useReducedMotion();
    const canAnimate = !reduced;
    const colorScheme = SEVERITY_COLORS[problem.severity];
    const isRevealed = reduced || isAnimated;

    const hasPrescription = problem.solutionStrategy ||
        (problem.actionPlan && problem.actionPlan.length > 0) ||
        (problem.imputationGuidance && problem.imputationGuidance.length > 0) ||
        (problem.healthScoreDimensionsImpacted && problem.healthScoreDimensionsImpacted.length > 0);

    // Action steps: show 2 initially, expand on demand
    const ACTION_STEPS_INITIAL = 2;
    const [showAllSteps, setShowAllSteps] = useState(false);

    // Severity-aware accent colors — brand-red themed
    const severityAccent = {
        critical: {
            labelBg: 'rgba(211,17,69,0.12)', labelColor: '#A4001D', labelBorder: 'rgba(211,17,69,0.30)',
            dotColor: '#D31145',
        },
        high: {
            labelBg: 'rgba(234,88,12,0.10)', labelColor: '#c2410c', labelBorder: 'rgba(234,88,12,0.25)',
            dotColor: '#ea580c',
        },
        medium: {
            labelBg: 'rgba(245,158,11,0.10)', labelColor: '#92400e', labelBorder: 'rgba(245,158,11,0.25)',
            dotColor: '#f59e0b',
        },
        low: {
            labelBg: 'rgba(100,116,139,0.08)', labelColor: '#475569', labelBorder: 'rgba(100,116,139,0.20)',
            dotColor: '#94a3b8',
        },
    }[problem.severity] || {
        labelBg: 'rgba(211,17,69,0.12)', labelColor: '#A4001D', labelBorder: 'rgba(211,17,69,0.30)',
        dotColor: '#D31145',
    };

    return (
        <div
            style={canAnimate ? {
                opacity: isRevealed ? 1 : 0,
                transform: isRevealed ? 'translateY(0)' : 'translateY(16px)',
                transition: mounted ? 'transform 0.5s ease-out, opacity 0.5s ease-out' : 'none',
                willChange: 'transform, opacity'
            } : {
                opacity: 1,
                transform: 'translateY(0)'
            }}
            data-testid="problem-card"
        >
            <div className={`rounded-2xl border overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 ${themeClasses.cardBackground} ${themeClasses.cardBorder} cursor-pointer`}>
                {/* Top severity indicator — brand gradient */}
                <div className="h-1" style={colorScheme.indicatorStyle} />

                {/* Header */}
                <div className="px-8 py-5 backdrop-blur-sm border-b border-gray-200/30" style={colorScheme.headerStyle}>
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <div className="flex items-center gap-3">
                                <h3 className={`text-xl font-bold ${themeClasses.headingText} leading-tight notranslate`} translate="no" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }} id={`problem-${problem.id}-title`}>
                                    {problem.issue || 'Untitled Issue'}
                                </h3>
                                <span
                                    className="notranslate inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide border"
                                    translate="no"
                                    style={{
                                        backgroundColor: severityAccent.labelBg,
                                        color: severityAccent.labelColor,
                                        borderColor: severityAccent.labelBorder,
                                    }}
                                >
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: severityAccent.dotColor }} />
                                    {problem.severity}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main content */}
                <div className={hasPrescription ? 'grid grid-cols-1 lg:grid-cols-2' : ''}>

                    {/* ── DIAGNOSIS Column ── */}
                    <div className="relative p-8">
                        <div className="flex items-center gap-3 mb-6">
                            <GrTest className="w-8 h-8" style={{ color: '#D31145' }} />
                            <h4 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'system-ui, -apple-system, sans-serif', color: '#1a1a1a' }}>{t('Diagnosis')}</h4>
                        </div>

                        <div className="space-y-5">
                            {/* Problem Details */}
                            <div className="relative pl-5 border-l-2" style={{ borderColor: 'rgba(211,17,69,0.25)' }}>
                                <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full" style={{ backgroundColor: '#D31145' }} />
                                <h5 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#1a1a1a' }}>{t('Problem Details')}</h5>
                                <BulletPointText
                                    text={problem.problemDetails || problem.businessImpact || 'Detailed analysis is being processed for this issue.'}
                                    className={`${themeClasses.bodyText} text-sm leading-relaxed`}
                                />
                            </div>

                            {/* Business Impact */}
                            {problem.businessImpact && problem.businessImpact !== problem.problemDetails && (
                                <div className="relative pl-5 border-l-2" style={{ borderColor: 'rgba(211,17,69,0.25)' }}>
                                    <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full" style={{ backgroundColor: '#D31145' }} />
                                    <h5 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: '#1a1a1a' }}>
                                        <svg className="w-3.5 h-3.5" style={{ color: '#D31145' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                                        {t('Business Impact')}
                                    </h5>
                                    <BulletPointText
                                        text={problem.businessImpact}
                                        className={`${themeClasses.bodyText} text-sm leading-relaxed`}
                                    />
                                </div>
                            )}

                            {/* Valid Examples */}
                            {problem.referenceValidExamples && problem.referenceValidExamples.length > 0 && (
                                <div className="relative pl-5 border-l-2" style={{ borderColor: 'rgba(211,17,69,0.25)' }}>
                                    <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full" style={{ backgroundColor: '#D31145' }} />
                                    <h5 className="text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5" style={{ color: '#1a1a1a' }}>
                                        <svg className="w-3.5 h-3.5" style={{ color: '#D31145' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                                        {t('Valid Examples')}
                                    </h5>
                                    <div className="flex flex-wrap gap-2">
                                        {problem.referenceValidExamples.slice(0, 5).map((example, i) => (
                                            <span key={i} className="notranslate px-3 py-1.5 rounded-lg text-xs font-mono border" translate="no" style={{ backgroundColor: 'rgba(211,17,69,0.05)', color: '#1a1a1a', borderColor: 'rgba(211,17,69,0.15)' }}>
                                                {example}
                                            </span>
                                        ))}
                                        {problem.referenceValidExamples.length > 5 && (
                                            <span className="text-gray-400 text-xs self-center px-2">
                                                +{problem.referenceValidExamples.length - 5} more
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── PRESCRIPTION Column ── */}
                    {hasPrescription && (
                        <div className={`relative border-l ${themeClasses.cardBorder}`}>
                            <div className="relative p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <FaFilePrescription className="w-8 h-8" style={{ color: '#D31145' }} />
                                    <h4 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'system-ui, -apple-system, sans-serif', color: '#1a1a1a' }}>{t('Prescription')}</h4>
                                </div>

                                <div className="space-y-5">
                                    {/* Solution */}
                                    {problem.solutionStrategy && (
                                        <div className="relative pl-5 border-l-2" style={{ borderColor: 'rgba(211,17,69,0.30)' }}>
                                            <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full" style={{ backgroundColor: '#D31145' }} />
                                            <h5 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: '#1a1a1a' }}>
                                                <svg className="w-3.5 h-3.5" style={{ color: '#D31145' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="2" /><circle cx="12" cy="12" r="6" strokeWidth="2" /><circle cx="12" cy="12" r="2" fill="currentColor" /></svg>
                                                {t('Solution')}
                                            </h5>
                                            <BulletPointText
                                                text={problem.solutionStrategy}
                                                className={`${themeClasses.bodyText} text-sm leading-relaxed`}
                                            />
                                        </div>
                                    )}

                                    {/* Health Score Impact */}
                                    {problem.healthScoreDimensionsImpacted && problem.healthScoreDimensionsImpacted.length > 0 && (
                                        <div className="relative pl-5 border-l-2" style={{ borderColor: '#D31145' }}>
                                            <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full" style={{ backgroundColor: '#D31145' }} />
                                            <h5 className="text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5" style={{ color: '#1a1a1a' }}>
                                                <svg className="w-3.5 h-3.5" style={{ color: '#D31145' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                                                {t('Health Score Impact')}
                                            </h5>
                                            <div className="flex flex-wrap gap-1.5">
                                                {problem.healthScoreDimensionsImpacted.map((dim, i) => (
                                                    <span
                                                        key={i}
                                                        className="notranslate inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
                                                        translate="no"
                                                        style={{
                                                            backgroundColor: 'rgba(211, 17, 69, 0.06)',
                                                            color: '#A4001D',
                                                            borderColor: 'rgba(211, 17, 69, 0.20)',
                                                        }}
                                                    >
                                                        <span className="inline-flex rounded-full h-1.5 w-1.5" style={{ backgroundColor: '#D31145' }} />
                                                        {dim}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Action Steps — Vertical Timeline (show 2, expandable) */}
                                    {problem.actionPlan && problem.actionPlan.length > 0 && (() => {
                                        const visibleSteps = showAllSteps ? problem.actionPlan : problem.actionPlan.slice(0, ACTION_STEPS_INITIAL);
                                        const hasMore = problem.actionPlan.length > ACTION_STEPS_INITIAL;
                                        return (
                                            <div>
                                                <h5 className="text-xs font-bold uppercase tracking-wider mb-4 flex items-center gap-1.5" style={{ color: '#1a1a1a' }}>
                                                    <svg className="w-3.5 h-3.5" style={{ color: '#D31145' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                                                    {t('Action Steps')}
                                                </h5>
                                                <div className="relative">
                                                    {/* Vertical timeline line */}
                                                    <div className="absolute left-[15px] top-4 bottom-4 w-px" style={{ background: 'linear-gradient(180deg, #D31145 0%, rgba(211,17,69,0.15) 100%)' }} />

                                                    <div className="space-y-0">
                                                        {visibleSteps.map((step, i) => (
                                                            <div key={i} className="relative flex items-start gap-4 group py-2.5">
                                                                {/* Timeline node */}
                                                                <div className="relative z-10 flex-shrink-0">
                                                                    <div
                                                                        className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shadow-sm"
                                                                        style={{
                                                                            background: 'linear-gradient(135deg, #D31145, #A4001D)',
                                                                            color: 'white',
                                                                            boxShadow: '0 0 0 2px rgba(211,17,69,0.12)',
                                                                        }}
                                                                    >
                                                                        {i + 1}
                                                                    </div>
                                                                </div>

                                                                {/* Step content */}
                                                                <div className={`flex-1 pb-1`}>
                                                                    <BulletPointText
                                                                        text={step}
                                                                        className={`${themeClasses.bodyText} text-sm leading-relaxed`}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* See More / See Less toggle */}
                                                {hasMore && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setShowAllSteps(prev => !prev); }}
                                                        className="mt-2 text-xs font-semibold flex items-center gap-1 hover:underline transition-colors"
                                                        style={{ color: '#D31145' }}
                                                    >
                                                        {showAllSteps
                                                            ? t('See Less')
                                                            : `${t('See More')} (${problem.actionPlan.length - ACTION_STEPS_INITIAL} ${t('more')})`
                                                        }
                                                        <svg className={`w-3 h-3 transition-transform ${showAllSteps ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {/* Implementation Guidance */}
                                    {problem.imputationGuidance && problem.imputationGuidance.length > 0 && (
                                        <div className="relative pl-5 border-l-2" style={{ borderColor: 'rgba(211,17,69,0.25)' }}>
                                            <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full" style={{ backgroundColor: '#D31145' }} />
                                            <h5 className="text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5" style={{ color: '#1a1a1a' }}>
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><circle cx="12" cy="12" r="3" strokeWidth="2" /></svg>
                                                {t('Implementation Guidance')}
                                            </h5>
                                            <ul className="space-y-2">
                                                {problem.imputationGuidance.map((g, i) => (
                                                    <li key={i} className={`flex items-start gap-2.5 ${themeClasses.bodyText} text-sm group`}>
                                                        <span className="mt-0.5 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" style={{ color: '#D31145' }}>→</span>
                                                        <span className="leading-relaxed flex-1 notranslate" translate="no">{g}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}